// @ts-check
import { formatDistanceToNow } from 'date-fns';
import { Download, ExternalLink, File, FileText, Image, Trash2 } from 'lucide-react';

const TaskAttachmentItem = ({ attachment, onDelete, currentUserId }) => {
  const canDelete = attachment.uploaded_by === currentUserId;

  const getFileIcon = (fileType) => {
    if (!fileType) return File;

    if (fileType.startsWith('image/')) return Image;
    if (fileType.includes('pdf') || fileType.includes('document')) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const FileIcon = getFileIcon(attachment.file_type);

  const handleDelete = async () => {
    if (!window.confirm('Delete this attachment?')) return;

    try {
      await onDelete(attachment.id);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      alert('Failed to delete attachment');
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-accent transition-colors">
      {/* File Icon */}
      <div className="flex-shrink-0 w-10 h-10 bg-card rounded-lg flex items-center justify-center border border-border">
        <FileIcon size={20} className="text-muted-foreground" />
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm text-foreground truncate">{attachment.file_name}</p>
          {attachment.file_size && (
            <span className="text-xs text-muted-foreground flex-shrink-0">{formatFileSize(attachment.file_size)}</span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Uploaded by {attachment.uploader_name || 'Unknown'} •{' '}
          {formatDistanceToNow(new Date(attachment.uploaded_at), { addSuffix: true })}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <a
          href={attachment.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-muted-foreground/70 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          title="Open"
        >
          <ExternalLink size={16} />
        </a>
        <a
          href={attachment.file_url}
          download={attachment.file_name}
          className="p-2 text-muted-foreground/70 hover:text-green-600 dark:hover:text-green-400 transition-colors"
          title="Download"
        >
          <Download size={16} />
        </a>
        {canDelete && (
          <button
            onClick={handleDelete}
            className="p-2 text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            title="Delete"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export default TaskAttachmentItem;
